/* Doc D-01 — Secondary-Market Dealer Term Sheet (Rule 144A / Reg S).
 * Visible on-screen variant of the term sheet that's also embedded in the
 * Closing Binder PDF. Indicative economics for qualified institutional buyers.
 * Iteration 15.
 */
import React from "react";
import { fmtCurrency, fmtDate } from "../../lib/format";
import { calc, DocFooter, Paper, makeSerial } from "./_shared";

export const DocD01DealerTermSheet = React.forwardRef(({ d }, ref) => {
  const { maturity, annualYield, discount, discountPct } = calc(d);
  const isin = "US" + String(Math.abs((d.faceValue || 0) * 17 + (d.couponRate || 0) * 23)).slice(0, 9).padStart(9, "0") + "8";
  const dealRef = makeSerial("TH-DEAL-" + (d.trustName || d.borrowerName || "X").replace(/\s/g, "").slice(0, 6).toUpperCase());

  return (
    <Paper ref={ref} footer={<DocFooter label="Doc D-01 — Secondary-Market Dealer Term Sheet · Rule 144A / Reg S" />}>
      {/* Title bar — keep visually distinct from the legal docs (Bloomberg-terminal feel) */}
      <div style={{
        background: "linear-gradient(135deg, #102968 0%, #0B4636 100%)",
        color: "#fff",
        padding: "18px 22px",
        margin: "-8px -8px 22px -8px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: "8.5pt", letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.85 }}>
            Indicative · Subject to Bilateral Negotiation
          </div>
          <div style={{ fontFamily: "var(--font-serif-display)", fontSize: "22pt", fontWeight: 600, marginTop: 4 }}>
            Dealer Term Sheet
          </div>
          <div style={{ fontSize: "10pt", opacity: 0.9, marginTop: 2 }}>
            Secondary-Market · {d.trustName || d.borrowerName}
          </div>
        </div>
        <div style={{ textAlign: "right", fontFamily: "var(--font-mono-fin)", fontSize: "9.5pt" }}>
          <div>DEAL REF: <strong>{dealRef}</strong></div>
          <div style={{ marginTop: 2 }}>ISIN: <strong>{isin}</strong></div>
          <div style={{ marginTop: 2 }}>AS OF: <strong>{fmtDate(d.executionDate)}</strong></div>
        </div>
      </div>

      {/* Disclaimer line (NOT a legal banner — purely a Rule 144A notice required for QIB-only docs) */}
      <p style={{ fontSize: "8.5pt", color: "#5C5C5C", fontStyle: "italic", marginBottom: 18, lineHeight: 1.4 }}>
        For the benefit of qualified institutional buyers under SEC Rule 144A and non-US persons under Regulation S.
        This term sheet summarizes the indicative economics and settlement mechanics for the Subject Note described
        in the accompanying Closing Binder. Not an offer to sell or solicitation of an offer to buy. All terms are
        subject to definitive documentation and bilateral negotiation at the time of trade.
      </p>

      {/* Three-column "trading screen" grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ border: "1px solid #E5E5DF", padding: 14, background: "#FAFAF6" }}>
          <div style={{ fontSize: "8pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#5C5C5C" }}>
            Face Principal
          </div>
          <div style={{ fontFamily: "var(--font-mono-fin)", fontSize: "20pt", fontWeight: 600, color: "#0B4636", marginTop: 4 }}>
            {fmtCurrency(d.faceValue, false)}
          </div>
        </div>
        <div style={{ border: "1px solid #E5E5DF", padding: 14, background: "#FAFAF6" }}>
          <div style={{ fontSize: "8pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#5C5C5C" }}>
            Discount Price
          </div>
          <div style={{ fontFamily: "var(--font-mono-fin)", fontSize: "20pt", fontWeight: 600, color: "#9E2A2B", marginTop: 4 }}>
            {fmtCurrency(d.purchasePrice, false)}
          </div>
          <div style={{ fontSize: "8.5pt", color: "#5C5C5C", marginTop: 2 }}>
            ({discountPct.toFixed(2)}% off maturity)
          </div>
        </div>
        <div style={{ border: "1px solid #E5E5DF", padding: 14, background: "#FAFAF6" }}>
          <div style={{ fontSize: "8pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#5C5C5C" }}>
            Implied APR
          </div>
          <div style={{ fontFamily: "var(--font-mono-fin)", fontSize: "20pt", fontWeight: 600, color: "#102968", marginTop: 4 }}>
            {annualYield.toFixed(2)}%
          </div>
          <div style={{ fontSize: "8.5pt", color: "#5C5C5C", marginTop: 2 }}>
            holding {d.termDays}d · Actual/365
          </div>
        </div>
      </div>

      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", marginBottom: 18 }}>
        <tbody>
          <tr><td style={{ width: "40%", fontWeight: 600 }}>Issuer (Maker)</td><td>{d.makerName}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Initial Holder</td><td>{d.trustName || d.borrowerName}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Original Face Principal</td><td>{fmtCurrency(d.faceValue)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Coupon</td><td>{Number(d.couponRate).toFixed(2)}% p.a. · Actual/365</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Term to Maturity</td><td>{d.termDays} days</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Maturity Date</td><td>{fmtDate(d.maturityDate)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Maturity Value</td><td>{fmtCurrency(maturity)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Indicative Discount</td><td>{fmtCurrency(discount)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Settlement Mechanism</td><td>DVP via Acceptance Bank · UCC § 3-302 HDC</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Eligibility</td><td>SEC Rule 144A QIBs · Reg S non-US persons</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Form</td><td>Negotiable instrument · UCC Art. 3 · physical chattel paper</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Acceptance Bank / Custodian</td><td>{d.acceptanceBankName}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Governing Law</td><td>{d.borrowerState || "Delaware"}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Rating</td><td>Not rated (unrated private instrument)</td></tr>
        </tbody>
      </table>

      <h3 style={{ fontSize: "11pt", fontWeight: 600, marginBottom: 6 }}>Settlement &amp; Custody</h3>
      <p style={{ fontSize: "10pt", lineHeight: 1.55 }}>
        Settlement <strong>T+0 same-day</strong> on confirmed Fedwire credit to Beneficiary Bank
        <strong> {d.sellerBankName || d.acceptanceBankName}</strong> ABA <strong>{d.sellerBankABA || "026009593"}</strong>.
        Each holder takes the instrument with full UCC § 3-302 holder-in-due-course protections, free of any equity
        or personal defense of the Maker. Custody by {d.acceptanceBankName} pursuant to the accompanying Custody
        Agreement (Doc T-05).
      </p>

      <h3 style={{ fontSize: "11pt", fontWeight: 600, margin: "14px 0 6px" }}>Trading Conventions</h3>
      <p style={{ fontSize: "10pt", lineHeight: 1.55 }}>
        Pricing quoted on a discount-yield basis. Bids and offers expressed in basis-point spread to the on-the-run
        91-day Treasury Bill benchmark (live Treasury rate stamped on every transaction event in the audit trail).
        Minimum trade size $250,000 face. Allocations subject to dealer discretion.
      </p>

      <div style={{ marginTop: 30, paddingTop: 14, borderTop: "1px solid #E5E5DF", display: "flex", justifyContent: "space-between", fontSize: "9pt", color: "#5C5C5C" }}>
        <div>Dealer of Record: <strong>{d.acceptanceBankName} — Discount &amp; Acceptance Desk</strong></div>
        <div>{d.acceptanceOfficer} · {d.acceptanceOfficerTitle}</div>
      </div>
    </Paper>
  );
});
